import React, { useContext, useEffect, useRef, useState } from 'react'
import gsap from 'gsap'
import { IProviderProps } from '../@types/context'
import {
  IData,
  IDataContext,
  IDataCopy,
  IVideoChapter,
  IVideoData,
} from '../@types/data'
import { IEnv } from '../@types/env'
import { useAnalytics } from './AnalyticsContext'
import { detect } from 'detect-browser'

const ANIMATION_DELAY = 8 // seconds

export const DataContext: React.Context<IDataContext> =
  React.createContext<IDataContext>({
    loading: true,
    isLowRes: false,
    copy: undefined,
    videos: [],
    selectedVideo: undefined,
    setSelectedVideo: () => null,
    requestedVideo: undefined,
    setRequestedVideo: () => null,
    activeChapter: undefined,
    setActiveChapter: () => null,
    requestedChapter: undefined,
    setRequestedChapter: () => null,
    nextChapter: undefined,
    startAutoplayTimer: () => null,
    cancelAutoplayTimer: () => null,
    resetTimer: () => null,
    timerProgress: 0,
  })

export const DataProvider: React.FC<IProviderProps> = ({ children }) => {
  const progressTween = useRef<{ percentage: number } | null>()
  const { setPage } = useAnalytics()
  const [isLowRes, setLowRes] = useState(false)
  const [loading, setLoading] = useState(true)
  const [copy, setCopy] = useState<IDataCopy>()
  const [videos, setVideos] = useState<IVideoData[]>()
  const [selectedVideo, setSelectedVideo] = useState<IVideoData>()
  const [requestedVideo, setRequestedVideo] = useState<IVideoData>()
  const [activeChapter, setActiveChapter] = useState<IVideoChapter>()
  const [nextChapter, setNextChapter] = useState<IVideoChapter | null>()
  const [requestedChapter, setRequestedChapter] = useState<IVideoChapter>()
  const [timerProgress, setTimerProgress] = useState(0)

  const {
    REACT_APP_CAR_TYPE,
    REACT_APP_COUNTRY,
    REACT_APP_LANGUAGE,
    REACT_APP_COPY_VERSION,
  } = process.env as unknown as IEnv

  useEffect(() => {
    const browser = detect()
    const lowRes =
      browser?.os?.toLowerCase().includes('win') === true ||
      browser?.os?.toLowerCase().includes('ios') === true ||
      browser?.os?.toLowerCase().includes('android') === true ||
      browser?.name.toLowerCase().includes('ie') === true ||
      browser?.name.toLowerCase().includes('edge') === true ||
      browser?.name.toLowerCase().includes('safari') === true
    setLowRes(lowRes)

    fetch(
      `./data/${REACT_APP_CAR_TYPE}-${REACT_APP_COUNTRY}-${REACT_APP_LANGUAGE}.json?cb=copy-v${REACT_APP_COPY_VERSION}`
    )
      .then(async (response: Response) => (await response.json()) as IData)
      .then((data: IData) => {
        setCopy(data.copy)
        setVideos(data.videos as IVideoData[])
        setLoading(false)
      })
      .catch((e) => {
        console.log(e)
      })
  }, [])

  useEffect(() => {
    if (selectedVideo === undefined || activeChapter === undefined) return
    resetTimer()
    const currentIndex = selectedVideo.chapters.indexOf(activeChapter)
    if (currentIndex < selectedVideo.chapters.length - 1) {
      const upcomingChapter = selectedVideo.chapters[currentIndex + 1]
      setNextChapter(upcomingChapter)
    } else {
      setNextChapter(null)
    }
  }, [activeChapter, selectedVideo])

  useEffect(() => {
    if (requestedVideo !== undefined) {
      setPage(requestedVideo.key)
    }
  }, [requestedVideo])

  useEffect(() => {
    resetTimer()
  }, [requestedChapter])

  const resetTimer = (): void => {
    cancelAutoplayTimer()
    setTimerProgress(0)
  }

  const startAutoplayTimer = (): void => {
    resetTimer()
    if (nextChapter == null) return
    progressTween.current = { percentage: 0 }
    gsap.to(progressTween.current, {
      percentage: 100,
      duration: ANIMATION_DELAY,
      ease: 'none',
      onUpdate: () => {
        if (progressTween.current != null) {
          setTimerProgress(progressTween.current.percentage)
        }
      },
      onComplete: () => {
        if (nextChapter != null) setRequestedChapter(nextChapter)
      },
    })
  }

  const cancelAutoplayTimer = (): void => {
    if (progressTween.current != null) {
      gsap.killTweensOf(progressTween.current)
      progressTween.current = null
    }
  }

  return (
    <DataContext.Provider
      value={{
        loading,
        isLowRes,
        copy,
        videos,
        selectedVideo,
        setSelectedVideo,
        requestedVideo,
        setRequestedVideo,
        activeChapter,
        setActiveChapter,
        requestedChapter,
        setRequestedChapter,
        nextChapter,
        startAutoplayTimer,
        cancelAutoplayTimer,
        resetTimer,
        timerProgress,
      }}
    >
      {children}
    </DataContext.Provider>
  )
}

export const useData = (): IDataContext => useContext(DataContext)
